import { StatusChip } from '@/components/ui/StatusChip';
import type { Agent } from '@/hooks/useAgents';
import { AGENT_THRESHOLDS } from '@/sections/agents/constants';
import { getAgentStatusProps } from '@/sections/agents/utils/agentStatus';

interface HealthCheckStatus {
  nestedVirtualization: { status: string; message: string };
  networkEgress: { status: string; message: string };
  diskSpace: { status: string; message: string };
  ram: { status: string; message: string };
}

export const getHealthCheckStatus = (agent: Agent): HealthCheckStatus => {
  if (!agent.health_check || !agent.os) {
    return {
      nestedVirtualization: { status: 'unknown', message: 'No data available' },
      networkEgress: { status: 'unknown', message: 'No data available' },
      diskSpace: { status: 'unknown', message: 'No data available' },
      ram: { status: 'unknown', message: 'No data available' },
    };
  }

  const { health_check, os } = agent;
  const isWindows = os === 'windows';

  const nestedVirtualization = {
    status:
      health_check.virtualization_supported === true ? 'success' : 'error',
    message:
      health_check.virtualization_supported === true
        ? 'Ready for full automation. Docker and Virtual Machine support enabled'
        : 'Nested virtualization not supported',
  };

  const networkEgress = {
    status: health_check.cloudflare === true ? 'success' : 'error',
    message:
      health_check.cloudflare === true
        ? 'Outbound network connection is healthy'
        : 'Network connectivity issues detected',
  };

  let diskSpace;
  if (health_check.disk_space !== undefined) {
    const minDiskSpace = isWindows
      ? AGENT_THRESHOLDS.MIN_DISK_SPACE_WINDOWS
      : AGENT_THRESHOLDS.MIN_DISK_SPACE_UNIX;
    const hasEnoughSpace = health_check.disk_space >= minDiskSpace;

    diskSpace = {
      status: hasEnoughSpace ? 'success' : 'error',
      message: hasEnoughSpace
        ? `System has sufficient disk space (${health_check.disk_space} GB available)`
        : `Low disk space detected. Free up space or add storage to meet minimum requirements (>= ${minDiskSpace} GB on ${isWindows ? 'Windows' : 'macOS/Linux'}).`,
    };
  } else {
    diskSpace = {
      status: 'unknown',
      message: 'Disk space information not available',
    };
  }

  let ram;
  if (health_check.memory !== undefined) {
    const minRam = isWindows
      ? AGENT_THRESHOLDS.MIN_RAM_WINDOWS
      : AGENT_THRESHOLDS.MIN_RAM_UNIX;
    const hasEnoughRam = health_check.memory >= minRam;

    ram = {
      status: hasEnoughRam ? 'success' : 'error',
      message: hasEnoughRam
        ? `Your system meets minimum RAM requirements (${health_check.memory} GB). System requires > ${minRam} GB on ${isWindows ? 'Windows' : 'Linux'}`
        : `Insufficient RAM detected (${health_check.memory} GB). System requires > ${minRam} GB on ${isWindows ? 'Windows' : 'Linux'}`,
    };
  } else {
    ram = {
      status: 'unknown',
      message: 'RAM information not available',
    };
  }

  return {
    nestedVirtualization,
    networkEgress,
    diskSpace,
    ram,
  };
};

export const getAgentHealthStatus = (agent: Agent | null) => {
  if (!agent) return null;

  const agentStatusProps = getAgentStatusProps(agent);
  if (!agentStatusProps) return null;

  return <StatusChip {...agentStatusProps} />;
};
